import { addMuiPaletteCompatibility } from './mui/MuiUtils';
import { createStyled } from '@mui/system';
import { createTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import designTokens from '@customink/design-tokens/build/ci_light/js/designTokens';
import accordionTheme from './components/Accordion/accordionTheme';
import alertTheme from './components/Alert/alertTheme';
import avatarTheme from './components/Avatar/avatarTheme';
import avatarGroupTheme from './components/Avatar/avatarGroupTheme';
import buttonTheme from './components/Button/buttonTheme';
import cardTheme from './components/Card/cardTheme';
import checkboxTheme from './components/Checkbox/checkboxTheme';
import chipTheme from './components/Chip/chipTheme';
import dialogTheme from './components/./DialogLegacy/dialogTheme';
import formControlTheme from './components/FormControl/formControlTheme';
import formControlLabelTheme from './components/FormControlLabel/formControlLabelTheme';
import formHelperTextTheme from './components/FormHelperText/formHelperTextTheme';
import formLabelTheme from './components/FormLabel/formLabelTheme';
import iconButtonTheme from './components/IconButton/iconButtonTheme';
import inputLabelTheme from './components/InputLabel/inputLabelTheme';
import linearProgressTheme from './components/LinearProgress/linearProgressTheme';
import linkTheme from './components/Link/linkTheme';
import listItemTheme from './components/ListItem/listItemTheme';
import menuTheme from './components/Menu/menuTheme';
import outlinedInputTheme from './components/TextField/outlinedInputTheme';
import radioTheme from './components/Radio/radioTheme';
import ratingTheme from './components/Rating/ratingTheme';
import selectTheme from './components/Select/selectTheme';
import tabsTheme from './components/Tabs/tabsTheme';
import textFieldTheme from './components/TextField/textFieldTheme';
import toggleTheme from './components/Toggle/toggleTheme';
import tooltipTheme from './components/Tooltip/tooltipTheme';
import tutorialTheme from './components/Tutorial/tutorialTheme';
import typographyTheme from './components/Typography/typographyTheme';
var color = designTokens.color, typography = designTokens.typography, breakpoints = designTokens.breakpoints;
/** Using two-step Theme composition as recommended on https://mui.com/customization/theming/.
 * First, define the base theme with palette and typography.
 * Second, let each component derive its theme on custominkBaseTheme.
 */
var custominkBaseTheme = createTheme({
    id: 'customink',
    breakpoints: {
        values: {
            sm: breakpoints.sm.value,
            md: breakpoints.md.value,
            lg: breakpoints.lg.value,
            xl: breakpoints.xl.value,
        },
    },
    palette: {
        red: {
            main: color.red.default.value,
            // light and dark need explicit definition, won't get calculated by MUI, see https://github.com/mui/material-ui/issues/29595
            light: 'rgb(251, 99, 51)',
            dark: color.red.hover.value,
            contrastText: color.shade.white.value,
        },
        blue: {
            main: color.blue.default.value,
            // light and dark need explicit definition, won't get calculated by MUI, see https://github.com/mui/material-ui/issues/29595
            light: 'rgb(75, 96, 219)',
            dark: color.blue.hover.value,
            contrastText: color.shade.white.value,
        },
        purple: {
            main: color.purple.default.value,
            light: color.purple.light.value,
            dark: color.purple.dark.value,
            contrastText: color.shade.white.value,
        },
        error: {
            main: color.status.error.value,
        },
        warning: {
            main: color.status.warning.value,
        },
        info: {
            main: color.status.info.value,
        },
        success: {
            main: color.status.success.value,
        },
    },
    typography: {
        fontFamily: [
            typography.family.value,
            'Helvetica',
            'Arial',
            'Trebuchet MS',
            'sans-serif',
        ].join(', '),
        h1: {
            fontSize: typography.heading.h1.size.value,
            lineHeight: typography.heading.h1.lineHeight.value,
        },
        h2: {
            fontSize: typography.heading.h2.size.value,
            lineHeight: typography.heading.h2.lineHeight.value,
        },
        h3: {
            fontSize: typography.heading.h3.size.value,
            lineHeight: typography.heading.h3.lineHeight.value,
        },
        h4: {
            fontSize: typography.heading.h4.size.value,
            lineHeight: typography.heading.h4.lineHeight.value,
        },
        h5: {
            fontSize: typography.heading.h5.size.value,
            lineHeight: typography.heading.h5.lineHeight.value,
        },
        h6: {
            fontSize: typography.heading.h6.size.value,
            lineHeight: typography.heading.h6.lineHeight.value,
        },
        subHeading: {
            fontSize: typography.heading.sub.size.value,
            lineHeight: typography.heading.sub.lineHeight.value,
        },
        bodyLong1: {
            fontSize: typography.body.long1.size.value,
            lineHeight: typography.body.long1.lineHeight.value,
        },
        bodyLong2: {
            fontSize: typography.body.long2.size.value,
            lineHeight: typography.body.long2.lineHeight.value,
        },
        bodyShort1: {
            fontSize: typography.body.short1.size.value,
            lineHeight: typography.body.short1.lineHeight.value,
        },
        bodyShort2: {
            fontSize: typography.body.short2.size.value,
            lineHeight: typography.body.short2.lineHeight.value,
        },
        captionRegular: {
            fontSize: typography.caption.captionRegular.size.value,
            lineHeight: typography.caption.captionRegular.lineHeight.value,
            letterSpacing: typography.caption.captionRegular.letterSpacing.value,
        },
        captionSmall: {
            fontSize: typography.caption.captionSmall.size.value,
            lineHeight: typography.caption.captionSmall.lineHeight.value,
            letterSpacing: typography.caption.captionSmall.letterSpacing.value,
        },
        captionLarge: {
            fontSize: typography.caption.captionLarge.size.value,
            lineHeight: typography.caption.captionLarge.lineHeight.value,
            letterSpacing: typography.caption.captionLarge.letterSpacing.value,
        },
        buttonSm: {
            fontSize: typography.button.small.size.value,
            lineHeight: typography.button.small.lineHeight.value,
        },
        buttonMd: {
            fontSize: typography.button.medium.size.value,
            lineHeight: typography.button.medium.lineHeight.value,
        },
        buttonLg: {
            fontSize: typography.button.large.size.value,
            lineHeight: typography.button.large.lineHeight.value,
        },
    },
    shape: {
        borderRadius: 6,
    },
});
//sets primary & secondary color
addMuiPaletteCompatibility(custominkBaseTheme);
var combineThemes = function (themeOptionsCreators) {
    // Performance note: multiple calling of the expensive `createTheme` is avoided by first combining all the themeOptions with `deepmerge`
    var mergedThemeOptions = themeOptionsCreators.reduce(function (acc, curr) { return deepmerge(acc, curr(custominkBaseTheme)); }, {});
    return createTheme(custominkBaseTheme, mergedThemeOptions);
};
var combinedTheme = combineThemes([
    accordionTheme,
    alertTheme,
    avatarTheme,
    avatarGroupTheme,
    buttonTheme,
    cardTheme,
    checkboxTheme,
    chipTheme,
    dialogTheme,
    formControlTheme,
    formControlLabelTheme,
    formHelperTextTheme,
    formLabelTheme,
    iconButtonTheme,
    inputLabelTheme,
    linearProgressTheme,
    linkTheme,
    listItemTheme,
    menuTheme,
    outlinedInputTheme,
    radioTheme,
    ratingTheme,
    selectTheme,
    tabsTheme,
    textFieldTheme,
    toggleTheme,
    tooltipTheme,
    tutorialTheme,
    typographyTheme,
]);
export var styled = createStyled({ defaultTheme: combinedTheme });
export default combinedTheme;
//# sourceMappingURL=themeCustomink.js.map